import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw, Upload, X, ChevronLeft, ChevronRight, ChevronDown, Check, Plus } from 'lucide-react';
import api from '../api';
import DraggableModal from '../components/DraggableModal';
import StatusBadge from '../components/StatusBadge';
import ActionButton from '../components/ActionButton';
import ErrorAlert from '../components/ErrorAlert';

const AGENT_CARD_STATUSES = ['not_agent_ready', 'ready_for_agent', 'paused', 'retired'];
const ADMIN_REVIEW_STATUSES = ['queued', 'in_review', 'approved', 'needs_more_info', 'rejected', 'retired'];
const CARD_ACTIONS = ['none', 'scrape_site', 'site_scraped', 'refresh_event_date', 'event_date_refreshed'];
const RETRIEVAL_LANES = ['recommendation', 'practical_find', 'companion_practical', 'local_directory', 'do_not_retrieve', 'retired'];
const RETRIEVAL_USES = ['pocket_plan_recommendation', 'pocket_plan_practical', 'companion_recommendation', 'companion_practical', 'local_directory'];
const FRESHNESS_RISK_OPTIONS = ['low', 'medium', 'high', 'event_date_required', 'seasonal_hours_required', 'source_stale', 'unknown'];

function normalizeOption(value) {
  return String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

function optionValue(option) {
  return typeof option === 'string' ? option : option?.value || '';
}

function optionLabel(option) {
  if (typeof option === 'string') return option.replace(/_/g, ' ');
  return option?.label || option?.value?.replace(/_/g, ' ') || '';
}

function Chips({ label, value, options, onChange }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{label}</label>
      <div className="flex flex-wrap gap-1.5">
        {options.map(s => (
          <button
            key={s}
            type="button"
            onClick={() => onChange(value === s ? '' : s)}
            className={`px-2.5 py-1 text-xs rounded-md font-medium border transition-all ${
              value === s ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-gray-600 border-gray-300 hover:border-slate-600'
            }`}
          >
            {s.replace(/_/g, ' ')}
          </button>
        ))}
      </div>
    </div>
  );
}

function SelectWithCreate({ label, value, options, onChange, onCreateOption, allowCreate = true }) {
  const [custom, setCustom] = useState('');
  const [creating, setCreating] = useState(false);
  const optionValues = new Set(options.map(optionValue));
  const allOptions = value && !optionValues.has(value) ? [...options, value] : options;
  const add = async () => {
    const raw = custom.trim();
    if (!raw) return;
    if (onCreateOption) {
      setCreating(true);
      try {
        const created = await onCreateOption(raw);
        onChange(created || raw);
      } finally {
        setCreating(false);
      }
    } else {
      onChange(normalizeOption(raw));
    }
    setCustom('');
  };

  return (
    <div>
      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{label}</label>
      <select
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      >
        <option value="">—</option>
        {allOptions.map(option => <option key={optionValue(option)} value={optionValue(option)}>{optionLabel(option)}</option>)}
      </select>
      {allowCreate && (
        <div className="flex gap-2 mt-2">
          <input
            value={custom}
            onChange={e => setCustom(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
            placeholder="Add new…"
            className="min-w-0 flex-1 px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <button type="button" onClick={add} disabled={creating} className="px-2 py-1.5 text-xs border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">
            {creating ? '…' : 'Add'}
          </button>
        </div>
      )}
    </div>
  );
}

function MultiOptionPicker({ label, selected, options, onChange, allowCreate = true }) {
  const [custom, setCustom] = useState('');
  const values = Array.isArray(selected) ? selected : [];
  const allOptions = [...options, ...values.filter(v => !options.includes(v))];
  const toggle = (option) => {
    onChange(values.includes(option) ? values.filter(v => v !== option) : [...values, option]);
  };
  const add = () => {
    const next = normalizeOption(custom);
    if (!next) return;
    if (!values.includes(next)) onChange([...values, next]);
    setCustom('');
  };

  return (
    <div>
      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{label}</label>
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {values.map(value => (
            <span key={value} className="flex items-center gap-1 px-2 py-0.5 bg-slate-800 text-white text-xs rounded-md">
              {value.replace(/_/g, ' ')}
              <button type="button" onClick={() => toggle(value)} className="hover:opacity-70"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}
      <div className="flex flex-wrap gap-1.5">
        {allOptions.map(option => (
          <button
            key={option}
            type="button"
            onClick={() => toggle(option)}
            className={`px-2.5 py-1 text-xs rounded-md font-medium border transition-all ${
              values.includes(option) ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-gray-600 border-gray-300 hover:border-slate-600'
            }`}
          >
            {option.replace(/_/g, ' ')}
          </button>
        ))}
      </div>
      {allowCreate && (
        <div className="flex gap-2 mt-2">
          <input
            value={custom}
            onChange={e => setCustom(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
            placeholder="Add new…"
            className="min-w-0 flex-1 px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <button type="button" onClick={add} className="px-2 py-1.5 text-xs border border-gray-300 rounded-md hover:bg-gray-50">Add</button>
        </div>
      )}
    </div>
  );
}

function TextField({ label, value, onChange, rows = 2, placeholder = '' }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{label}</label>
      <textarea
        rows={rows}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        placeholder={placeholder}
      />
    </div>
  );
}

function TagMultiSelect({ tags, selected, onChange, onCreateTag }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = tags.filter(t =>
    (t.display_label || t.name).toLowerCase().includes(q.toLowerCase())
  );

  const toggle = (name) => {
    onChange(selected.includes(name) ? selected.filter(t => t !== name) : [...selected, name]);
  };
  const normalizedQuery = normalizeOption(q);
  const exactMatch = tags.some(t => t.name === normalizedQuery);
  const create = async () => {
    if (!normalizedQuery || !onCreateTag) return;
    setCreating(true);
    try {
      const tag = await onCreateTag(normalizedQuery);
      if (!selected.includes(tag.name)) onChange([...selected, tag.name]);
      setQ('');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div ref={ref} className="relative">
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {selected.map(name => {
            const tag = tags.find(t => t.name === name);
            return (
              <span key={name} className="flex items-center gap-1 px-2 py-0.5 bg-slate-800 text-white text-xs rounded-md">
                {tag?.display_label || name.replace(/_/g, ' ')}
                <button type="button" onClick={() => toggle(name)} className="hover:opacity-70"><X className="w-3 h-3" /></button>
              </span>
            );
          })}
        </div>
      )}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg text-sm text-left hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <span className={selected.length ? 'text-gray-700' : 'text-gray-400'}>
          {selected.length ? `${selected.length} tag${selected.length !== 1 ? 's' : ''} selected` : 'Select tags…'}
        </span>
        <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <input
              autoFocus
              type="text"
              placeholder="Filter tags…"
              value={q}
              onChange={e => setQ(e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <ul className="max-h-52 overflow-y-auto">
            {filtered.map(t => {
              const active = selected.includes(t.name);
              return (
                <li key={t.name}>
                  <button
                    type="button"
                    onClick={() => toggle(t.name)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left hover:bg-gray-50 ${active ? 'text-gray-900 font-medium' : 'text-gray-700'}`}
                  >
                    <span className={`w-4 h-4 flex-shrink-0 rounded border flex items-center justify-center ${active ? 'bg-slate-800 border-slate-800' : 'border-gray-300'}`}>
                      {active && <Check className="w-3 h-3 text-white" />}
                    </span>
                    {t.display_label || t.name.replace(/_/g, ' ')}
                  </button>
                </li>
              );
            })}
            {normalizedQuery && !exactMatch && (
              <li>
                <button
                  type="button"
                  disabled={creating}
                  onClick={create}
                  className="w-full px-3 py-2 text-sm text-left text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                >
                  Create “{normalizedQuery.replace(/_/g, ' ')}”
                </button>
              </li>
            )}
            {filtered.length === 0 && <li className="px-3 py-2 text-sm text-gray-400">No tags match</li>}
          </ul>
        </div>
      )}
    </div>
  );
}

function applyWorkflowCoupling(next) {
  if (
    next.agent_card_status === 'retired' ||
    next.admin_review_status === 'retired' ||
    next.retrieval_lane === 'retired'
  ) {
    return {
      ...next,
      agent_card_status: 'retired',
      admin_review_status: 'retired',
      retrieval_lane: 'retired',
    };
  }
  if (next.admin_review_status === 'rejected') {
    return { ...next, agent_card_status: 'paused' };
  }
  if (next.retrieval_lane === 'do_not_retrieve' && next.agent_card_status === 'ready_for_agent') {
    return { ...next, agent_card_status: 'paused' };
  }
  return next;
}

const MODAL_TABS = ['Details', 'Taxonomy', 'Proposals', 'Actions', 'Review Queue', 'Signals'];

function cardDataToForm(card) {
  return {
    card_key: card.card_key || 'primary',
    place_name: card.place_name || '',
    address: card.address || '',
    phone: card.phone || '',
    email: card.email || '',
    website_url: card.website_url || '',
    source_url: card.source_url || '',
    area: card.area || '',
    town_or_community: card.town_or_community || '',
    sector_primary: card.sector_primary || '',
    category_type: card.category_type || '',
    category: card.card_category || card.category || '',
    use_mode_primary: card.use_mode_primary || '',
    agent_card_status: card.agent_card_status || '',
    admin_review_status: card.admin_review_status || '',
    admin_recommended_action: card.admin_recommended_action || '',
    admin_notes: card.admin_notes || '',
    card_judgment_needed: Boolean(card.card_judgment_needed),
    card_fact_warning: card.card_fact_warning || '',
    guest_use_case_tags: card.guest_use_case_tags || [],
    card_title: card.card_title || card.place_name || '',
    pitch: card.pitch || card.display_pitch || '',
    short_desc_guest: card.short_desc_guest || '',
    good_for: card.good_for || '',
    suits: card.suits || '',
    caveat: card.caveat || '',
    card_note_for_agent: card.card_note_for_agent || '',
    recommendation_angle: card.recommendation_angle || '',
    booking_note: card.booking_note || '',
    seasonal_note: card.seasonal_note || '',
    weather_note: card.weather_note || '',
    budget_note: card.budget_note || '',
    access_note: card.access_note || '',
    confidence: card.confidence || '',
    freshness_risk: card.freshness_risk || '',
    retrieval_lane: card.retrieval_lane || '',
    retrieval_uses: card.retrieval_uses || [],
    utility_kind: card.utility_kind || '',
    practical_note: card.practical_note || '',
    booking_logic: card.booking_logic || [],
    seasonality: card.seasonality || '',
    weather_logic: card.weather_logic || [],
    budget: card.budget || '',
    parking_access: card.parking_access || [],
    fact_warnings: card.fact_warnings || [],
    action_result_note: card.action_result_note || '',
    action_error: card.action_error || '',
  };
}

function ModalTabs({ active, onChange, proposalCount }) {
  return (
    <div className="flex border-b border-gray-200 bg-white flex-shrink-0">
      {MODAL_TABS.map(tab => (
        <button
          key={tab}
          type="button"
          onClick={() => onChange(tab)}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            active === tab
              ? 'border-slate-800 text-slate-800'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          {tab}
          {tab === 'Proposals' && proposalCount > 0 && (
            <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-orange-100 text-orange-700 rounded-full">{proposalCount}</span>
          )}
        </button>
      ))}
    </div>
  );
}

function PlaceCardModal({ card, onClose, onSave, onReplace, availableTags, onCreateTag, vocab, onGoToChanges }) {
  const [detail, setDetail] = useState(card);
  const [tab, setTab] = useState('Details');
  const [selectedCardKey, setSelectedCardKey] = useState(card.card_key || 'primary');
  const [cardOptions, setCardOptions] = useState(card.cards || []);
  const [form, setForm] = useState(cardDataToForm(card));
  const [newCard, setNewCard] = useState({ card_key: '', card_title: '', retrieval_lane: 'recommendation', category: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const setWorkflowField = (field, value) => {
    setForm(f => applyWorkflowCoupling({ ...f, [field]: value }));
  };

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.getPlaceCard(card.place_master_id, selectedCardKey),
      api.getCardsForPlace(card.place_master_id).catch(() => []),
    ])
      .then(([data, cards]) => {
        if (!alive) return;
        setDetail(data);
        setForm(cardDataToForm(data));
        setCardOptions((data.cards && data.cards.length ? data.cards : cards) || []);
        if ((data.card_key || 'primary') !== selectedCardKey) setSelectedCardKey(data.card_key || 'primary');
      })
      .catch(e => { if (alive) setError(e.message); });
    return () => { alive = false; };
  }, [card.place_master_id, selectedCardKey]);

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const updated = await onSave(card.place_master_id, form, selectedCardKey);
      setDetail(updated);
      setSelectedCardKey(updated.card_key || selectedCardKey);
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const approve = async () => {
    setSaving(true);
    setError('');
    try {
      await onSave(card.place_master_id, form, selectedCardKey);
      const updated = await api.approvePlaceCard(card.place_master_id, 'admin approved card', selectedCardKey);
      setDetail(updated);
      setSelectedCardKey(updated.card_key || selectedCardKey);
      onReplace(updated);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    setSaving(true);
    setError('');
    try {
      await onSave(card.place_master_id, form, selectedCardKey);
      const updated = await api.publishPlaceCard(card.place_master_id, 'admin published card', selectedCardKey);
      setDetail(updated);
      setSelectedCardKey(updated.card_key || selectedCardKey);
      onReplace(updated);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const approveAndPublish = async () => {
    setSaving(true);
    setError('');
    try {
      await onSave(card.place_master_id, form, selectedCardKey);
      const updated = await api.approveAndPublishPlaceCard(card.place_master_id, 'admin approved and published card', selectedCardKey);
      setDetail(updated);
      setSelectedCardKey(updated.card_key || selectedCardKey);
      onReplace(updated);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const addCard = async () => {
    if (!newCard.card_key.trim()) {
      setError('Card key is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const created = await api.addCardForPlace(card.place_master_id, newCard);
      const cards = await api.getCardsForPlace(card.place_master_id);
      setCardOptions(cards);
      setNewCard({ card_key: '', card_title: '', retrieval_lane: 'recommendation', category: '' });
      setSelectedCardKey(created.card_key || 'primary');
      onReplace({ ...detail, place_master_id: card.place_master_id, card_count: cards.length });
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const readiness = detail.publish_readiness || {};
  const proposalCount = detail.pending_change_request_count || 0;

  return (
    <DraggableModal isOpen onClose={onClose} title={detail.place_name || card.place_name} initialWidth={Math.round(window.innerWidth * 0.65)} initialHeight={Math.round(window.innerHeight * 0.88)}>
      <div className="flex flex-col h-full">
        <ModalTabs active={tab} onChange={setTab} proposalCount={proposalCount} />

        <div className="flex-1 overflow-y-auto p-6">
          {error && <ErrorAlert error={error} onClose={() => setError('')} />}

          {tab === 'Details' && (
            <div className="max-w-2xl space-y-5">
              {detail.card_judgment_needed && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2">
                  <p className="text-xs text-yellow-800 font-medium">Judgment needed</p>
                </div>
              )}
              {(detail.card_fact_warning || detail.operator_review_note) && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-2">
                  {detail.card_fact_warning && <p className="text-xs text-orange-800 font-medium mb-1">{detail.card_fact_warning}</p>}
                  {detail.operator_review_note && <p className="text-xs text-gray-700">{detail.operator_review_note}</p>}
                </div>
              )}
              <div className="flex items-center gap-3">
                <p className="text-xs text-gray-400 font-mono">{detail.place_master_id}</p>
                {detail.card_count > 1 && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{detail.card_count} cards</span>
                )}
              </div>
              <div className="rounded-lg border border-gray-200 p-3 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Editing card</label>
                    <select
                      value={selectedCardKey}
                      onChange={e => setSelectedCardKey(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {(cardOptions.length ? cardOptions : [{ card_key: form.card_key || 'primary', card_title: form.card_title }]).map(option => (
                        <option key={option.card_key} value={option.card_key}>
                          {option.card_key.replace(/_/g, ' ')}{option.publish_status ? ` · ${option.publish_status}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Add card</label>
                    <div className="flex gap-2">
                      <input
                        value={newCard.card_key}
                        onChange={e => setNewCard(c => ({ ...c, card_key: e.target.value }))}
                        placeholder="golf"
                        className="min-w-0 flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <button type="button" onClick={addCard} disabled={saving} className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">Add</button>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input
                    value={newCard.card_title}
                    onChange={e => setNewCard(c => ({ ...c, card_title: e.target.value }))}
                    placeholder="Optional card title"
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <select
                    value={newCard.retrieval_lane}
                    onChange={e => setNewCard(c => ({ ...c, retrieval_lane: e.target.value }))}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {RETRIEVAL_LANES.filter(lane => lane !== 'retired').map(lane => (
                      <option key={lane} value={lane}>{lane.replace(/_/g, ' ')}</option>
                    ))}
                  </select>
                  <SelectWithCreate label="New card category" value={newCard.category} options={vocab.category || []} onChange={v => setNewCard(c => ({ ...c, category: v }))} allowCreate={false} />
                </div>
              </div>
              <TextField label="Card key" value={form.card_key} onChange={v => setForm(f => ({ ...f, card_key: v }))} rows={1} placeholder="primary" />
              <TextField label="Place name" value={form.place_name} onChange={v => setForm(f => ({ ...f, place_name: v }))} rows={1} />
              <div className="grid grid-cols-2 gap-4">
                <TextField label="Area" value={form.area} onChange={v => setForm(f => ({ ...f, area: v }))} rows={1} />
                <TextField label="Town / community" value={form.town_or_community} onChange={v => setForm(f => ({ ...f, town_or_community: v }))} rows={1} />
              </div>
              <TextField label="Address" value={form.address} onChange={v => setForm(f => ({ ...f, address: v }))} rows={1} />
              <div className="grid grid-cols-2 gap-4">
                <TextField label="Phone" value={form.phone} onChange={v => setForm(f => ({ ...f, phone: v }))} rows={1} />
                <TextField label="Email" value={form.email} onChange={v => setForm(f => ({ ...f, email: v }))} rows={1} />
              </div>
              <TextField label="Website" value={form.website_url} onChange={v => setForm(f => ({ ...f, website_url: v }))} rows={1} />
              <TextField label="Source URL" value={form.source_url} onChange={v => setForm(f => ({ ...f, source_url: v }))} rows={1} />
              <hr className="border-gray-100" />
              <TextField label="Card title" value={form.card_title} onChange={v => setForm(f => ({ ...f, card_title: v }))} rows={1} />
              <TextField label="Pitch" value={form.pitch} onChange={v => setForm(f => ({ ...f, pitch: v }))} rows={4} />
              <TextField label="Short guest copy" value={form.short_desc_guest} onChange={v => setForm(f => ({ ...f, short_desc_guest: v }))} rows={4} />
              <TextField label="Knows" value={form.caveat} onChange={v => setForm(f => ({ ...f, caveat: v }))} rows={3} />
              <TextField label="Agent guidance" value={form.card_note_for_agent} onChange={v => setForm(f => ({ ...f, card_note_for_agent: v }))} rows={3} />
              <div className={`text-xs font-medium pt-1 ${readiness.publishable ? 'text-green-700' : 'text-orange-600'}`}>
                {readiness.publishable ? 'Publishable' : 'Not publishable'}
                {(readiness.missing || []).length > 0 && <span className="text-gray-400 font-normal ml-2">Missing: {readiness.missing.join(', ')}</span>}
                {(readiness.blockers || []).length > 0 && <span className="text-gray-400 font-normal ml-2">Blocked: {readiness.blockers.join(', ')}</span>}
              </div>
            </div>
          )}

          {tab === 'Taxonomy' && (
            <div className="max-w-2xl space-y-5">
              <SelectWithCreate label="Card Category" value={form.category} options={vocab.category || []} onChange={v => setForm(f => ({ ...f, category: v }))} allowCreate={false} />
              <div className="grid grid-cols-2 gap-4">
                <SelectWithCreate label="Place Sector" value={form.sector_primary} options={vocab.sector || []} onChange={v => setForm(f => ({ ...f, sector_primary: v }))} allowCreate={false} />
                <SelectWithCreate label="Place Category" value={form.category_type} options={vocab.category_type || []} onChange={v => setForm(f => ({ ...f, category_type: v }))} allowCreate={false} />
              </div>
              {availableTags?.length > 0 && (
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Card tags</label>
                  <TagMultiSelect tags={availableTags} selected={form.guest_use_case_tags} onChange={v => setForm(f => ({ ...f, guest_use_case_tags: v }))} onCreateTag={onCreateTag} />
                </div>
              )}
              <hr className="border-gray-100" />
              {form.retrieval_lane === 'practical_find' && (
                <>
                  <SelectWithCreate label="Utility kind" value={form.utility_kind} options={vocab.utility_kind || []} onChange={v => setForm(f => ({ ...f, utility_kind: v }))} allowCreate={false} />
                  <TextField label="Practical note" value={form.practical_note} onChange={v => setForm(f => ({ ...f, practical_note: v }))} rows={2} />
                </>
              )}
              <div className="grid grid-cols-2 gap-4">
                <SelectWithCreate label="Seasonality" value={form.seasonality} options={vocab.seasonality || []} onChange={v => setForm(f => ({ ...f, seasonality: v }))} allowCreate={false} />
                <SelectWithCreate label="Budget" value={form.budget} options={vocab.budget || []} onChange={v => setForm(f => ({ ...f, budget: v }))} allowCreate={false} />
              </div>
              <MultiOptionPicker label="Booking logic" selected={form.booking_logic} options={(vocab.booking_logic || []).map(t => t.value)} onChange={v => setForm(f => ({ ...f, booking_logic: v }))} allowCreate={false} />
              <MultiOptionPicker label="Weather logic" selected={form.weather_logic} options={(vocab.weather_logic || []).map(t => t.value)} onChange={v => setForm(f => ({ ...f, weather_logic: v }))} allowCreate={false} />
              <MultiOptionPicker label="Arrival & parking" selected={form.parking_access} options={(vocab.parking_access || []).map(t => t.value)} onChange={v => setForm(f => ({ ...f, parking_access: v }))} allowCreate={false} />
              <SelectWithCreate label="Freshness risk" value={form.freshness_risk} options={FRESHNESS_RISK_OPTIONS} onChange={v => setForm(f => ({ ...f, freshness_risk: v }))} />
              <MultiOptionPicker label="Fact warnings" selected={form.fact_warnings} options={(vocab.fact_warning_label || []).map(t => t.value)} onChange={v => setForm(f => ({ ...f, fact_warnings: v }))} allowCreate={false} />
            </div>
          )}

          {tab === 'Proposals' && (
            <div className="max-w-2xl">
              {proposalCount === 0 ? (
                <p className="text-sm text-gray-400">No pending proposals.</p>
              ) : (
                <div className="flex flex-col items-start gap-3">
                  <p className="text-sm text-gray-700">{proposalCount} pending proposal{proposalCount !== 1 ? 's' : ''} for this card.</p>
                  <button
                    type="button"
                    onClick={() => { onClose(); onGoToChanges?.(detail?.place_master_id); }}
                    className="px-4 py-2 text-sm bg-slate-800 text-white rounded-lg hover:bg-slate-700"
                  >
                    Go to Change Proposals →
                  </button>
                </div>
              )}
            </div>
          )}

          {tab === 'Actions' && (
            <div className="max-w-2xl space-y-5">
              <Chips label="Action" value={form.admin_recommended_action} options={CARD_ACTIONS} onChange={v => setForm(f => ({ ...f, admin_recommended_action: v }))} />
              <div className="grid grid-cols-2 gap-4">
                <TextField label="Action result note" value={form.action_result_note} onChange={v => setForm(f => ({ ...f, action_result_note: v }))} rows={4} />
                <TextField label="Action error" value={form.action_error} onChange={v => setForm(f => ({ ...f, action_error: v }))} rows={4} />
              </div>
            </div>
          )}

          {tab === 'Review Queue' && (
            <div className="max-w-2xl space-y-5">
              <Chips label="Agent card status" value={form.agent_card_status} options={AGENT_CARD_STATUSES} onChange={v => setWorkflowField('agent_card_status', v)} />
              <Chips label="Admin review" value={form.admin_review_status} options={ADMIN_REVIEW_STATUSES} onChange={v => setWorkflowField('admin_review_status', v)} />
              <Chips label="Retrieval lane" value={form.retrieval_lane} options={RETRIEVAL_LANES} onChange={v => setWorkflowField('retrieval_lane', v)} />
              <MultiOptionPicker label="Retrieval uses" selected={form.retrieval_uses} options={RETRIEVAL_USES} onChange={v => setForm(f => ({ ...f, retrieval_uses: v }))} allowCreate={false} />
              <div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={form.card_judgment_needed} onChange={e => setForm(f => ({ ...f, card_judgment_needed: e.target.checked }))} className="rounded border-gray-300" />
                  Judgment needed
                </label>
              </div>
              <TextField label="Admin notes" value={form.admin_notes} onChange={v => setForm(f => ({ ...f, admin_notes: v }))} rows={3} placeholder="Internal notes…" />
            </div>
          )}
        </div>

        <div className="flex gap-3 px-6 py-3 border-t border-gray-100 flex-shrink-0 bg-white">
          <ActionButton variant="primary" onClick={save} loading={saving}>Save</ActionButton>
          <ActionButton variant="secondary" onClick={approveAndPublish} loading={saving}>Approve & Publish</ActionButton>
          <ActionButton variant="secondary" onClick={approve} loading={saving}>Approve</ActionButton>
          <ActionButton variant="secondary" onClick={publish} loading={saving}>Publish</ActionButton>
          <ActionButton variant="secondary" onClick={onClose}>Close</ActionButton>
        </div>
      </div>
    </DraggableModal>
  );
}

function AddPlaceModal({ onClose, onCreate, availableTags, onCreateTag, vocab }) {
  const [form, setForm] = useState({
    place_name: '',
    retrieval_lane: 'recommendation',
    area: '',
    town_or_community: '',
    sector_primary: '',
    category_type: '',
    category: '',
    website_url: '',
    source_url: '',
    address: '',
    phone: '',
    email: '',
    guest_use_case_tags: [],
    pitch: '',
    utility_kind: '',
    practical_note: '',
    admin_notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!form.place_name.trim()) {
      setError('Place name is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onCreate(form);
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <DraggableModal isOpen onClose={onClose} title="Add Listing" initialWidth={680} initialHeight={Math.round(window.innerHeight * 0.78)}>
      <div className="p-5 space-y-4 overflow-y-auto h-full">
        {error && <ErrorAlert error={error} onClose={() => setError('')} />}
        <TextField label="Place name" value={form.place_name} onChange={v => setForm(f => ({ ...f, place_name: v }))} rows={1} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Chips
            label="Retrieval lane"
            value={form.retrieval_lane}
            options={RETRIEVAL_LANES.filter(lane => lane !== 'retired')}
            onChange={v => setForm(f => ({ ...f, retrieval_lane: v }))}
          />
          <TextField label="Area" value={form.area} onChange={v => setForm(f => ({ ...f, area: v }))} rows={1} />
        </div>
        <TextField label="Town / community" value={form.town_or_community} onChange={v => setForm(f => ({ ...f, town_or_community: v }))} rows={1} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextField label="Website" value={form.website_url} onChange={v => setForm(f => ({ ...f, website_url: v }))} rows={1} />
          <TextField label="Source" value={form.source_url} onChange={v => setForm(f => ({ ...f, source_url: v }))} rows={1} />
        </div>
        <TextField label="Address" value={form.address} onChange={v => setForm(f => ({ ...f, address: v }))} rows={1} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextField label="Phone" value={form.phone} onChange={v => setForm(f => ({ ...f, phone: v }))} rows={1} />
          <TextField label="Email" value={form.email} onChange={v => setForm(f => ({ ...f, email: v }))} rows={1} />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Card tags</label>
          <TagMultiSelect
            tags={availableTags}
            selected={form.guest_use_case_tags}
            onChange={v => setForm(f => ({ ...f, guest_use_case_tags: v }))}
            onCreateTag={onCreateTag}
          />
        </div>
        {form.retrieval_lane === 'practical_find' && (
          <>
            <SelectWithCreate label="Utility kind" value={form.utility_kind} options={vocab.utility_kind || []} onChange={v => setForm(f => ({ ...f, utility_kind: v }))} allowCreate={false} />
            <TextField label="Practical note" value={form.practical_note} onChange={v => setForm(f => ({ ...f, practical_note: v }))} rows={2} />
          </>
        )}
        <TextField label="Pitch" value={form.pitch} onChange={v => setForm(f => ({ ...f, pitch: v }))} rows={3} />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <SelectWithCreate label="Place Sector" value={form.sector_primary} options={vocab.sector || []} onChange={v => setForm(f => ({ ...f, sector_primary: v }))} allowCreate={false} />
          <SelectWithCreate label="Place Category" value={form.category_type} options={vocab.category_type || []} onChange={v => setForm(f => ({ ...f, category_type: v }))} allowCreate={false} />
          <SelectWithCreate label="Initial Card Category" value={form.category} options={vocab.category || []} onChange={v => setForm(f => ({ ...f, category: v }))} allowCreate={false} />
        </div>
        <TextField label="Admin notes" value={form.admin_notes} onChange={v => setForm(f => ({ ...f, admin_notes: v }))} rows={3} placeholder="Internal notes…" />
        <div className="flex gap-3 pt-2 border-t border-gray-100">
          <ActionButton variant="primary" onClick={submit} loading={saving}>Create</ActionButton>
          <ActionButton variant="secondary" onClick={onClose}>Cancel</ActionButton>
        </div>
      </div>
    </DraggableModal>
  );
}

// ── CSV Import Modal ─────────────────────────────────────────────────────────
function FileInput({ files, setFiles, label, key_, accepts }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{label}</label>
      <div className="flex items-center gap-2">
        <input
        type="file"
        accept={accepts || '.csv'}
        onChange={e => setFiles(f => ({ ...f, [key_]: e.target.files[0] || null }))}
        className="text-sm text-gray-600 file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border file:border-gray-300 file:bg-white file:text-sm file:text-gray-700 hover:file:bg-gray-50"
      />
      {files[key_] && (
        <button onClick={() => setFiles(f => ({ ...f, [key_]: null }))} className="text-gray-400 hover:text-gray-600">
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
    {files[key_] && <p className="text-xs text-gray-500 mt-1">{files[key_].name}</p>}
  </div>
  );
}

function ImportModal({ onClose, onImported }) {
  const [files, setFiles] = useState({ master: null, admin: null, signals: null });
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!files.master && !files.admin && !files.signals) {
      setError('Select at least one file');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const fd = new FormData();
      if (files.master) fd.append('master_csv', files.master);
      if (files.admin) fd.append('admin_csv', files.admin);
      if (files.signals) fd.append('signals_csv', files.signals);
      const res = await api.importPlaces(fd);
      setResult(res);
      onImported();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DraggableModal isOpen onClose={onClose} title="Import Place CSVs" initialWidth={520} initialHeight={440}>
      <div className="p-6 space-y-5 overflow-y-auto h-full">
        {error && <ErrorAlert error={error} onClose={() => setError('')} />}
        {result ? (
          <div className="space-y-3">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-1">
              <p className="text-sm font-medium text-green-800">Import successful</p>
              <p className="text-xs text-green-700">Places upserted: {result.places_upserted}</p>
              <p className="text-xs text-green-700">Admin cards upserted: {result.admin_cards_upserted}</p>
              {result.admin_cards_from_queue !== undefined && <p className="text-xs text-green-700">From admin queue: {result.admin_cards_from_queue}</p>}
              {result.admin_cards_derived_from_master !== undefined && <p className="text-xs text-green-700">Derived from master: {result.admin_cards_derived_from_master}</p>}
              {result.working_cards_upserted !== undefined && <p className="text-xs text-green-700">Working cards upserted: {result.working_cards_upserted}</p>}
              <p className="text-xs text-green-700">Signals reloaded: {result.signals_reloaded}</p>
            </div>
            <ActionButton variant="secondary" onClick={onClose}>Close</ActionButton>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-600">Upload any combination. Master can be skipped if already imported.</p>
            <FileInput files={files} setFiles={setFiles} label="Master CSV (places)" key_="master" />
            <FileInput files={files} setFiles={setFiles} label="Admin queue CSV (place_admin_cards)" key_="admin" />
            <FileInput files={files} setFiles={setFiles} label="Signal proposals CSV" key_="signals" />
            <div className="flex gap-3 pt-2 border-t border-gray-100">
              <ActionButton variant="primary" onClick={run} loading={loading} icon={Upload}>
                {loading ? 'Importing…' : 'Import'}
              </ActionButton>
              <ActionButton variant="secondary" onClick={onClose}>Cancel</ActionButton>
            </div>
          </>
        )}
      </div>
    </DraggableModal>
  );
}

function ConflictReportModal({ report, onClose, onRepair, repairing }) {
  const items = report?.items || [];
  const repaired = report?.fields_repaired !== undefined;
  return (
    <DraggableModal isOpen onClose={onClose} title="Verify Place/Card Copies" initialWidth={760} initialHeight={560}>
      <div className="p-6 space-y-5 overflow-y-auto h-full">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-gray-900">Copied fields should match the canonical place record.</p>
            <p className="text-xs text-gray-500 mt-1">Repair updates only place name, area, sector, lat, and lng on stale working cards.</p>
          </div>
          {items.length > 0 && (
            <button
              type="button"
              onClick={onRepair}
              disabled={repairing}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-slate-900 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${repairing ? 'animate-spin' : ''}`} />
              {repairing ? 'Repairing…' : 'Repair Copies'}
            </button>
          )}
        </div>

        {repaired && (
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
            <p className="text-sm font-medium text-blue-900">
              Repaired {report.fields_repaired} copied field{report.fields_repaired === 1 ? '' : 's'} on {report.cards_repaired} card{report.cards_repaired === 1 ? '' : 's'}.
            </p>
          </div>
        )}

        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-lg border border-gray-200 p-3">
            <p className="text-xs text-gray-500 uppercase font-semibold">Scanned cards</p>
            <p className="text-2xl font-semibold text-gray-900">{report?.scanned_cards ?? 0}</p>
          </div>
          <div className="rounded-lg border border-gray-200 p-3">
            <p className="text-xs text-gray-500 uppercase font-semibold">Cards with conflicts</p>
            <p className="text-2xl font-semibold text-gray-900">{report?.cards_with_conflicts ?? 0}</p>
          </div>
          <div className="rounded-lg border border-gray-200 p-3">
            <p className="text-xs text-gray-500 uppercase font-semibold">Conflict fields</p>
            <p className="text-2xl font-semibold text-gray-900">{report?.conflict_count ?? 0}</p>
          </div>
        </div>

        {items.length === 0 ? (
          <div className="rounded-lg border border-green-200 bg-green-50 p-4">
            <p className="text-sm font-medium text-green-800">No place/card copy conflicts found.</p>
          </div>
        ) : (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Place</th>
                  <th className="px-3 py-2 text-left font-medium">Card</th>
                  <th className="px-3 py-2 text-left font-medium">Field</th>
                  <th className="px-3 py-2 text-left font-medium">Card value</th>
                  <th className="px-3 py-2 text-left font-medium">Place value</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.slice(0, 100).map((item, idx) => (
                  <tr key={`${item.place_card_id}-${item.field}-${idx}`}>
                    <td className="px-3 py-2">
                      <p className="font-medium text-gray-900">{item.place_name || item.place_master_id}</p>
                      <p className="text-xs text-gray-400 font-mono">{item.place_master_id}</p>
                    </td>
                    <td className="px-3 py-2 text-gray-600">{item.card_key || 'primary'}</td>
                    <td className="px-3 py-2 text-gray-700 font-medium">{item.field}</td>
                    <td className="px-3 py-2 text-gray-600 break-all">{String(item.card_value ?? '—')}</td>
                    <td className="px-3 py-2 text-gray-600 break-all">{String(item.place_value ?? '—')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {items.length > 100 && (
              <p className="px-3 py-2 text-xs text-gray-500 bg-gray-50">Showing first 100 of {items.length} conflicts.</p>
            )}
          </div>
        )}
      </div>
    </DraggableModal>
  );
}

const PAGE_SIZE = 100;

// ── Place Cards View ─────────────────────────────────────────────────────────
export default function PlaceCardsView({ onGoToChanges }) {
  const [cards, setCards] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showConflictReport, setShowConflictReport] = useState(false);
  const [conflictReport, setConflictReport] = useState(null);
  const [conflictLoading, setConflictLoading] = useState(false);
  const [conflictRepairing, setConflictRepairing] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [availableTags, setAvailableTags] = useState([]);
  const [vocab, setVocab] = useState({});
  const loadSeq = useRef(0);

  useEffect(() => {
    api.getTags().then(data => setAvailableTags(data.filter(t => t.active && t.vocabulary_status !== 'pending'))).catch(() => {});
    ['sector', 'category_type', 'category', 'utility_kind', 'seasonality', 'budget',
      'booking_logic', 'weather_logic', 'parking_access', 'fact_warning_label'].forEach(family => {
      api.getVocabularyTerms(family, 'approved')
        .then(data => setVocab(v => ({ ...v, [family]: data })))
        .catch(() => {});
    });
  }, []);

  const load = useCallback(async (pg, q, sf) => {
    const seq = loadSeq.current + 1;
    loadSeq.current = seq;
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset: pg * PAGE_SIZE };
      const query = String(q || '').trim();
      if (query) params.search = query;
      if (sf) params.status = sf;
      const res = await api.getPlaceCards(params);
      if (seq !== loadSeq.current) return;
      setCards(res.items ?? res);
      setTotal(res.total ?? (res.items ?? res).length);
      setError('');
    } catch (e) {
      if (seq !== loadSeq.current) return;
      setError(e.message);
    } finally {
      if (seq !== loadSeq.current) return;
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(page, search, statusFilter); }, [load, page, search, statusFilter]);
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0);
      setSearch(searchInput.trim());
    }, 250);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleStatusFilter = (val) => { setStatusFilter(val); setPage(0); };

  const handleSave = async (id, body, cardKey = 'primary') => {
    const updated = await api.patchPlaceCard(id, body, cardKey);
    setCards(cs => cs.map(c => {
      if (c.place_master_id !== id) return c;
      if ((cardKey || 'primary') === 'primary') return { ...c, ...updated };
      return {
        ...c,
        admin_review_status: updated.admin_review_status,
        agent_card_status: updated.agent_card_status,
        card_count: updated.card_count ?? c.card_count,
      };
    }));
    return updated;
  };

  const handleReplace = updated => {
    setCards(cs => cs.map(c => {
      if (c.place_master_id !== updated.place_master_id) return c;
      if ((updated.card_key || 'primary') === 'primary') return { ...c, ...updated };
      return {
        ...c,
        admin_review_status: updated.admin_review_status,
        agent_card_status: updated.agent_card_status,
        card_count: updated.card_count ?? c.card_count,
      };
    }));
  };

  const handleCreatePlace = async (body) => {
    const created = await api.createPlaceCard(body);
    setCards(cs => [created, ...cs.filter(c => c.place_master_id !== created.place_master_id)]);
    setTotal(t => t + 1);
    setSelectedId(created.place_master_id);
    return created;
  };

  const handleCreateTag = async (name) => {
    const tag = await api.createTag({ name, display_label: name.replace(/_/g, ' ') });
    setAvailableTags(tags => [...tags.filter(t => t.name !== tag.name), tag].sort((a, b) => a.name.localeCompare(b.name)));
    return tag;
  };

  const runConflictScan = async () => {
    setConflictLoading(true);
    setError('');
    try {
      const report = await api.getPlaceCardDenormalizedConflicts({ limit: '5000' });
      setConflictReport(report);
      setShowConflictReport(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setConflictLoading(false);
    }
  };

  const repairConflicts = async () => {
    setConflictRepairing(true);
    setError('');
    try {
      const report = await api.repairPlaceCardDenormalizedConflicts({ limit: '5000' });
      setConflictReport(report);
      load(page, search, statusFilter);
    } catch (e) {
      setError(e.message);
    } finally {
      setConflictRepairing(false);
    }
  };

  const selected = cards.find(c => c.place_master_id === selectedId) ?? null;
  const pageStart = page * PAGE_SIZE + 1;
  const pageEnd = Math.min(page * PAGE_SIZE + cards.length, total);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Place Cards</h1>
          <p className="text-sm text-gray-500">{total} total</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-slate-900 text-white border border-slate-900 rounded-lg hover:bg-slate-800"
          >
            <Plus className="w-4 h-4" />
            Add Listing
          </button>
          <button
            onClick={() => setShowImport(true)}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Upload className="w-4 h-4" />
            Import CSVs
          </button>
          <button
            onClick={runConflictScan}
            disabled={conflictLoading}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${conflictLoading ? 'animate-spin' : ''}`} />
            Verify Copies
          </button>
          <button onClick={() => load(page, searchInput.trim(), statusFilter)} className="p-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
            <RefreshCw className="w-4 h-4 text-gray-600" />
          </button>
        </div>
      </div>

      {error && <ErrorAlert error={error} />}

      {/* Filters */}
      <div className="flex gap-3">
        <input
          type="text"
          placeholder="Search name, area, ID…"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          className="w-full max-w-sm px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <select
          value={statusFilter}
          onChange={e => handleStatusFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">All review statuses</option>
          {ADMIN_REVIEW_STATUSES.map(status => (
            <option key={status} value={status}>{status.replace(/_/g, ' ')}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-400 text-sm">Loading…</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left font-medium">Name</th>
                <th className="px-4 py-3 text-left font-medium">Place Sector</th>
                <th className="px-4 py-3 text-left font-medium">Area</th>
                <th className="px-4 py-3 text-left font-medium">Lane</th>
                <th className="px-4 py-3 text-left font-medium">Priority</th>
                <th className="px-4 py-3 text-left font-medium">Review</th>
                <th className="px-4 py-3 text-left font-medium">Card status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {cards.map(c => (
                <tr key={c.place_master_id} onClick={() => setSelectedId(c.place_master_id)} className="hover:bg-gray-50 cursor-pointer">
                  <td className="px-4 py-3 font-medium text-gray-900">
                    {c.place_name}
                    {c.card_judgment_needed && <span className="ml-2 text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded">review</span>}
                    {c.card_count > 1 && <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">{c.card_count} cards</span>}
                    {c.pending_change_request_count > 0 && (
                      <span className="ml-2 text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">
                        change request{c.pending_change_request_count > 1 ? ` ${c.pending_change_request_count}` : ''}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{c.sector_primary || '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{c.area || '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{c.retrieval_lane || '—'}</td>
                  <td className="px-4 py-3 text-gray-700 font-medium">{c.priority_band || '—'}</td>
                  <td className="px-4 py-3">
                    {c.admin_review_status && <StatusBadge status={c.admin_review_status} />}
                  </td>
                  <td className="px-4 py-3">
                    {c.agent_card_status && <StatusBadge status={c.agent_card_status} />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!loading && cards.length === 0 && <p className="text-center py-10 text-gray-400 text-sm">No place cards found</p>}
        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50">
            <span className="text-xs text-gray-500">{pageStart}–{pageEnd} of {total}</span>
            <div className="flex gap-1">
              <button onClick={() => setPage(p => p - 1)} disabled={page === 0} className="p-1.5 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed">
                <ChevronLeft className="w-4 h-4 text-gray-600" />
              </button>
              <button onClick={() => setPage(p => p + 1)} disabled={pageEnd >= total} className="p-1.5 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed">
                <ChevronRight className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          </div>
        )}
      </div>

      {selected && (
        <PlaceCardModal
          card={selected}
          onClose={() => setSelectedId(null)}
          onSave={handleSave}
          onReplace={handleReplace}
          availableTags={availableTags}
          onCreateTag={handleCreateTag}
          vocab={vocab}
          onGoToChanges={onGoToChanges}
        />
      )}
      {showCreate && (
        <AddPlaceModal
          onClose={() => setShowCreate(false)}
          onCreate={handleCreatePlace}
          availableTags={availableTags}
          onCreateTag={handleCreateTag}
          vocab={vocab}
        />
      )}
      {showImport && (
        <ImportModal onClose={() => setShowImport(false)} onImported={() => load(0, search, statusFilter)} />
      )}
      {showConflictReport && (
        <ConflictReportModal
          report={conflictReport}
          onClose={() => setShowConflictReport(false)}
          onRepair={repairConflicts}
          repairing={conflictRepairing}
        />
      )}
    </div>
  );
}
